import { invalidate } from "@react-three/fiber";
import gsap from "gsap";
import { Fragment, useEffect, useMemo } from "react";
import { useRef } from "react";
import { type Group, MathUtils } from "three";
import { RoundedBoxGeometry } from "three/examples/jsm/geometries/RoundedBoxGeometry";
import { match } from "ts-pattern";
import { useCabinetStore } from "../../../context/StoreContext";
import { getDimensions } from "../../../hooks/getDimensions";
import { CompartmentModus, useCanvasStore } from "../../../hooks/useCanvasStore";
import { useMaterialContext } from "../context/MaterialContext";
import { CORNER_SMOOTHING, getCornerRounding } from "../data";
import type { CompartmentType, DoorType as DoorTypes } from "../data/Compartment";
import type { Door as DoorType } from "../data/Door";
import useCompartment from "../hooks/useCompartment";
import OverlayMaterial from "./Compartment/OverlayMaterial";

interface Props {
	compartment: CompartmentType;
	door: DoorTypes;
}

export function Door({ door, compartment }: Props) {
	return match(door)
		.with({ type: "single" }, (door) => <DoorRenderer compartment={compartment} door={door} />)
		.with({ type: "double" }, (door) => (
			<Fragment>
				<DoorRenderer compartment={compartment} door={door.left} hasSibling={!!door.right} />
				<DoorRenderer compartment={compartment} door={door.right} hasSibling={!!door.left} />
			</Fragment>
		))
		.otherwise(() => null);
}

interface DoorRendererProps {
	door: DoorType;
	compartment: CompartmentType;
	hasSibling?: boolean;
}

function DoorRenderer({ door, compartment, hasSibling }: DoorRendererProps) {
	const { state, api } = useCabinetStore();
	const { getMaterial } = useMaterialContext();
	const cornerRounding: number = getCornerRounding(state, api);

	const { allCompartmentsOpen, compartmentMode, fullScreen } = useCanvasStore();
	const { isHovered } = useCompartment(compartment);
	const dimensions = useMemo(() => getDimensions(door), [door]);

	const x = door.x / 1000 + dimensions.depth / 2;
	const y = door.y / 1000 + dimensions.width / 2;
	const z = door.z / 1000 + dimensions.height / 2;

	// const invalidate = useThree((selector) => selector.invalidate);

	const doorRotation = door.side === "left" ? MathUtils.degToRad(20) : -MathUtils.degToRad(20);

	const _opacity = compartmentMode === CompartmentModus.AddShelf ? 0.75 : 1;

	const startRotation = door.side === "left" ? MathUtils.degToRad(90) : -MathUtils.degToRad(90);

	const doorGeometry = useMemo(() => {
		return new RoundedBoxGeometry(
			dimensions.width,
			dimensions.height,
			dimensions.depth,
			CORNER_SMOOTHING,
			cornerRounding,
		);
	}, [dimensions, cornerRounding]);

	const hoverGeometry = useMemo(
		() =>
			new RoundedBoxGeometry(
				dimensions.width,
				dimensions.height,
				dimensions.depth,
				CORNER_SMOOTHING,
				cornerRounding,
			),
		[dimensions, cornerRounding],
	);

	const material = getMaterial({
		variant: hasSibling ? "double-door" : "door",
		width: door.width,
		height: door.height,
		depth: door.depth,
	});

	const groupRef = useRef<Group>(null);

	useEffect(() => {
		if (!groupRef.current) return;

		const targetRotation = isHovered || allCompartmentsOpen ? doorRotation : startRotation;

		const tween = gsap.to(groupRef.current.rotation, {
			y: targetRotation,
			duration: 0.6,
			ease: "power2.out",
			onUpdate: () => invalidate(),
		});

		return () => {
			tween.kill();
		};
	}, [isHovered, allCompartmentsOpen, doorRotation, startRotation]);

	return (
		<Fragment>
			<group
				ref={groupRef}
				rotation={[0, startRotation, Math.PI / 2]}
				position={
					door.side === "left" ? [x - dimensions.depth / 2, y, z] : [x + dimensions.depth / 2, y, z]
				}
			>
				<mesh
					position-z={dimensions.depth / 2}
					name={door.id}
					receiveShadow
					castShadow
					material={material}
					geometry={doorGeometry}
				/>

				{!fullScreen && (
					<mesh position-z={dimensions.depth / 2} geometry={hoverGeometry}>
						<OverlayMaterial compartment={compartment} />
					</mesh>
				)}
			</group>
		</Fragment>
	);
}
